import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/MainLayout';
import api from '../../lib/api';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '../../components/ui/tooltip';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import {
  DollarSign, FileText, Building2, Truck, Zap, Flame, Droplets, Trash2,
  RefreshCw, Filter, X, TrendingUp,
  Sparkles, ArrowUpRight, Calendar, Wifi, Loader2, Info,
  ChevronsDown, Hash, Receipt, Users, Trophy,
} from 'lucide-react';

const COMMODITY_COLORS = {
  Electricity: '#f59e0b',
  Electric: '#f59e0b',
  Gas: '#ef4444',
  Water: '#3b82f6',
  Sewer: '#8b5cf6',
  Trash: '#78716c',
  Internet: '#06b6d4',
  Telecom: '#14b8a6',
  Cable: '#ec4899',
  StormWater: '#6366f1',
  Stormwater: '#6366f1',
  Fire: '#dc2626',
  FireProtection: '#dc2626',
  ManagementFee: '#64748b',
  Telephone: '#0891b2',
  Other: '#94a3b8',
};

const COMMODITY_ICONS = {
  Electricity: Zap,
  Electric: Zap,
  Gas: Flame,
  Water: Droplets,
  Sewer: Droplets,
  Trash: Trash2,
  Internet: Wifi,
  Telecom: Wifi,
};

const fmt = (n) => {
  if (n == null) return '$0';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
};

const fmtFull = (n) =>
  n != null ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$0.00';

const fmtShortDate = (d) => {
  if (!d) return '—';
  try {
    const date = new Date(d);
    return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: '2-digit' });
  } catch { return d; }
};

// Small sparkline-esque commodity breakdown bar
function CommodityMiniBar({ commodities, total }) {
  if (!commodities || !total) return null;
  const entries = Object.entries(commodities)
    .map(([k, v]) => ({ name: k, value: v.spend || 0 }))
    .filter(e => e.value > 0)
    .sort((a, b) => b.value - a.value);
  return (
    <div className="flex h-1.5 rounded-full overflow-hidden bg-muted" data-testid="commodity-minibar">
      {entries.map((e) => (
        <div
          key={e.name}
          style={{
            width: `${(e.value / total) * 100}%`,
            backgroundColor: COMMODITY_COLORS[e.name] || '#94a3b8',
          }}
          title={`${e.name}: ${fmtFull(e.value)}`}
        />
      ))}
    </div>
  );
}

export default function BillsDashboardPage() {
  const navigate = useNavigate();
  const [currentAccount, setCurrentAccount] = useState(() => {
    const str = localStorage.getItem('current_account');
    return str ? JSON.parse(str) : null;
  });

  useEffect(() => {
    const check = () => {
      const str = localStorage.getItem('current_account');
      const acc = str ? JSON.parse(str) : null;
      if (acc?.id && acc.id !== currentAccount?.id) setCurrentAccount(acc);
    };
    check();
    const iv = setInterval(check, 1000);
    const to = setTimeout(() => clearInterval(iv), 10000);
    return () => { clearInterval(iv); clearTimeout(to); };
  }, [currentAccount?.id]);

  const accountId = currentAccount?.id;

  const [summary, setSummary] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [vendors, setVendors] = useState(null);
  const [filters, setFilters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  // Active filters
  const [vendor, setVendor] = useState('');
  const [commodity, setCommodity] = useState('');
  const [state, setState] = useState('');
  const [customer, setCustomer] = useState('');

  // Spend by Property (lazy-loaded)
  const [properties, setProperties] = useState([]);
  const [propMeta, setPropMeta] = useState({ total: 0, has_more: false, avg_spend: 0, max_spend: 0 });
  const [propLoading, setPropLoading] = useState(false);
  const propOffsetRef = useRef(0);

  // Recent bills by property (lazy-loaded)
  const [recent, setRecent] = useState([]);
  const [recentMeta, setRecentMeta] = useState({ total: 0, has_more: false });
  const [recentLoading, setRecentLoading] = useState(false);
  const recentOffsetRef = useRef(0);

  const buildParams = useCallback(() => {
    const p = { account_id: accountId };
    if (vendor) p.vendor = vendor;
    if (commodity) p.commodity = commodity;
    if (state) p.state = state;
    if (customer) p.customer = customer;
    return p;
  }, [accountId, vendor, commodity, state, customer]);

  const fetchTopline = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    const params = buildParams();
    try {
      const [sumRes, monthRes, vendRes] = await Promise.all([
        api.get('/bills/dashboard/summary', { params }),
        api.get('/bills/dashboard/monthly-spend', { params: { ...params, months: 12 } }),
        api.get('/bills/dashboard/top-vendors', { params: { ...params, limit: 15 } }),
      ]);
      setSummary(sumRes.data);
      setMonthly(monthRes.data);
      setVendors(vendRes.data);
    } catch (err) {
      console.error('Dashboard fetch error', err);
    } finally {
      setLoading(false);
    }
  }, [accountId, buildParams]);

  const fetchPropertiesPage = useCallback(async (reset = false) => {
    if (!accountId) return;
    const offset = reset ? 0 : propOffsetRef.current;
    setPropLoading(true);
    try {
      const { data } = await api.get('/bills/dashboard/spend-by-property', {
        params: { ...buildParams(), offset, limit: 10 },
      });
      setProperties(prev => (reset ? data.properties : [...prev, ...data.properties]));
      setPropMeta({
        total: data.total,
        has_more: data.has_more,
        avg_spend: data.avg_spend,
        max_spend: data.max_spend,
        commodity_leaders: data.commodity_leaders || [],
      });
      propOffsetRef.current = offset + data.properties.length;
    } catch (err) { /* silent */ }
    finally { setPropLoading(false); }
  }, [accountId, buildParams]);

  const fetchRecentPage = useCallback(async (reset = false) => {
    if (!accountId) return;
    const offset = reset ? 0 : recentOffsetRef.current;
    setRecentLoading(true);
    try {
      const { data } = await api.get('/bills/dashboard/recent-bills-by-property', {
        params: { ...buildParams(), offset, limit: 10 },
      });
      setRecent(prev => (reset ? data.items : [...prev, ...data.items]));
      setRecentMeta({ total: data.total, has_more: data.has_more });
      recentOffsetRef.current = offset + data.items.length;
    } catch (err) { /* silent */ }
    finally { setRecentLoading(false); }
  }, [accountId, buildParams]);

  const fetchFilters = useCallback(async () => {
    if (!accountId) return;
    try {
      const { data } = await api.get('/bills/dashboard/filters', { params: { account_id: accountId } });
      setFilters(data);
    } catch {}
  }, [accountId]);

  // Initial + filter-change loaders
  useEffect(() => { fetchTopline(); }, [fetchTopline]);
  useEffect(() => { fetchFilters(); }, [fetchFilters]);
  useEffect(() => {
    propOffsetRef.current = 0;
    fetchPropertiesPage(true);
    recentOffsetRef.current = 0;
    fetchRecentPage(true);
  }, [fetchPropertiesPage, fetchRecentPage]);

  const activeFilterCount = [vendor, commodity, state, customer].filter(Boolean).length;
  const clearFilters = () => { setVendor(''); setCommodity(''); setState(''); setCustomer(''); };

  const topCommodities = (summary?.by_commodity || []).slice(0, 6);
  const pieData = topCommodities.map(c => ({ name: c.commodity, value: c.total_spend }));

  const goToBills = (params) => {
    const qs = new URLSearchParams({ account_id: accountId, ...params }).toString();
    navigate(`/bills?${qs}`);
  };

  if (!accountId) return <MainLayout wide><div className="p-8 text-center text-muted-foreground">No account selected</div></MainLayout>;

  return (
    <MainLayout wide>
      <TooltipProvider delayDuration={200}>
        <div className="space-y-4 pb-8" data-testid="bills-dashboard">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-sm">
                <Receipt className="h-4 w-4 text-white" strokeWidth={2.5} />
              </div>
              <div>
                <h1 className="text-xl font-bold tracking-tight" data-testid="dashboard-title">Bills Dashboard</h1>
                <p className="text-xs text-muted-foreground mt-0.5">Utility spend, property benchmarks, and bill activity</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={showFilters ? 'default' : 'outline'}
                size="sm"
                className="h-8 text-xs"
                onClick={() => setShowFilters(!showFilters)}
                data-testid="toggle-filters-btn"
              >
                <Filter className="h-3.5 w-3.5 mr-1" />
                Filters
                {activeFilterCount > 0 && <Badge className="ml-1 h-4 px-1 text-[9px] bg-white/20">{activeFilterCount}</Badge>}
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={fetchTopline} data-testid="refresh-dashboard-btn">
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>

          {/* Filter Bar */}
          {showFilters && filters && (
            <div className="bg-card border rounded-md p-3 flex flex-wrap items-center gap-3" data-testid="filter-bar">
              <Select value={state || '_all'} onValueChange={v => setState(v === '_all' ? '' : v)}>
                <SelectTrigger className="h-8 w-32 text-xs" data-testid="filter-state"><SelectValue placeholder="State" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">All States</SelectItem>
                  {(filters.states || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={commodity || '_all'} onValueChange={v => setCommodity(v === '_all' ? '' : v)}>
                <SelectTrigger className="h-8 w-36 text-xs" data-testid="filter-commodity"><SelectValue placeholder="Commodity" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">All Commodities</SelectItem>
                  {(filters.commodities || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={vendor || '_all'} onValueChange={v => setVendor(v === '_all' ? '' : v)}>
                <SelectTrigger className="h-8 w-44 text-xs" data-testid="filter-vendor"><SelectValue placeholder="Vendor" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">All Vendors</SelectItem>
                  {(filters.vendors || []).map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
              {activeFilterCount > 0 && (
                <Button variant="ghost" size="sm" className="h-8 text-xs text-muted-foreground" onClick={clearFilters} data-testid="clear-filters-btn">
                  <X className="h-3 w-3 mr-1" /> Clear
                </Button>
              )}
            </div>
          )}

          {/* ─── Row 1 · KPI Cards ─── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="summary-cards">
            <KpiCard
              label="Total Spend"
              value={summary ? fmtFull(summary.total_spend) : '—'}
              icon={<DollarSign className="h-4 w-4" />}
              sub={summary ? (
                <span className="flex flex-col gap-0.5 text-[10px]" data-testid="total-spend-split">
                  <span className="flex items-center gap-1">
                    <span className="inline-block h-1 w-1 rounded-full bg-emerald-500" />
                    <span className="text-muted-foreground">New Charges:</span>
                    <span className="font-semibold font-mono">{fmt(summary.new_charges_total || 0)}</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="inline-block h-1 w-1 rounded-full bg-blue-500" />
                    <span className="text-muted-foreground">Total Amount Due:</span>
                    <span className="font-semibold font-mono">{fmt(summary.total_spend || 0)}</span>
                  </span>
                </span>
              ) : null}
              tip="New Charges = sum of current-period charges (no prior balances, late fees, deposits). Total Amount Due = full billed amount including carry-over balances."
              accent="from-emerald-500 to-emerald-600"
              testId="kpi-total-spend"
            />
            <KpiCard
              label="Total Bills"
              value={summary ? summary.total_bills.toLocaleString() : '—'}
              icon={<FileText className="h-4 w-4" />}
              sub={summary ? `${summary.paid_count.toLocaleString()} paid · ${summary.unpaid_count.toLocaleString()} unpaid` : ''}
              tip="Paid/Unpaid counts are live from Payment File transactions (authoritative source)."
              accent="from-blue-500 to-blue-600"
              testId="kpi-total-bills"
            />
            <KpiCard
              label="Properties"
              value={summary ? (summary.total_locations ?? '—').toLocaleString() : '—'}
              icon={<Building2 className="h-4 w-4" />}
              sub={summary ? (
                <span className="flex flex-col gap-0.5 text-[10px]" data-testid="properties-split">
                  <span className="flex items-center gap-1">
                    <span className="text-muted-foreground">Units:</span>
                    <span className="font-semibold">{(summary.total_units ?? summary.total_spaces ?? 0).toLocaleString()}</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-2.5 w-2.5 text-muted-foreground opacity-60 cursor-help" data-testid="units-info" />
                      </TooltipTrigger>
                      <TooltipContent className="text-xs max-w-[240px]">
                        Units = spaces in the portfolio from UBM Onboarding &gt; Units (ubm_spaces).
                      </TooltipContent>
                    </Tooltip>
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="text-muted-foreground">Meters:</span>
                    <span className="font-semibold">{(summary.total_meters || 0).toLocaleString()}</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-2.5 w-2.5 text-muted-foreground opacity-60 cursor-help" data-testid="meters-info" />
                      </TooltipTrigger>
                      <TooltipContent className="text-xs max-w-[240px]">
                        Meters from UBM Onboarding &gt; Meters (ubm_meters collection).
                      </TooltipContent>
                    </Tooltip>
                  </span>
                </span>
              ) : null}
              tip="Properties = physical locations (buildings/communities) in your portfolio."
              accent="from-violet-500 to-violet-600"
              testId="kpi-properties"
            />
            <KpiCard
              label="Vendors"
              value={summary ? summary.total_vendors : '—'}
              icon={<Users className="h-4 w-4" />}
              sub={summary ? (
                <span className="flex flex-col gap-0.5 text-[10px]" data-testid="vendors-split">
                  <span className="flex items-center gap-1">
                    <span className="text-muted-foreground">VCR accounts:</span>
                    <span className="font-semibold">{(summary.vcr_account_count || 0).toLocaleString()}</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-2.5 w-2.5 text-muted-foreground opacity-60 cursor-help" data-testid="vcr-info" />
                      </TooltipTrigger>
                      <TooltipContent className="text-xs max-w-[260px]">
                        VCR (Unit Accounts) from UBM Onboarding &gt; Vendors &gt; Unit Accounts tab (ubm_unit_accounts).
                      </TooltipContent>
                    </Tooltip>
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="text-muted-foreground">CA accounts:</span>
                    <span className="font-semibold">{(summary.ca_account_count || 0).toLocaleString()}</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-2.5 w-2.5 text-muted-foreground opacity-60 cursor-help" data-testid="ca-info" />
                      </TooltipTrigger>
                      <TooltipContent className="text-xs max-w-[260px]">
                        CA (Utility Accounts) from UBM Onboarding &gt; Vendors &gt; Utility Accounts (CA) tab (ubm_utility_accounts).
                      </TooltipContent>
                    </Tooltip>
                  </span>
                </span>
              ) : null}
              tip="Distinct vendors billing the portfolio. VCR + CA counts come from UBM Vendor Accounts."
              accent="from-amber-500 to-amber-600"
              testId="kpi-vendors"
            />
          </div>

          {/* ─── Row 2 · Commodity Cards (full-width responsive) ─── */}
          {topCommodities.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3" data-testid="commodity-cards">
              {topCommodities.map((c) => {
                const Icon = COMMODITY_ICONS[c.commodity] || Zap;
                const color = COMMODITY_COLORS[c.commodity] || '#94a3b8';
                const pct = summary?.total_spend ? (c.total_spend / summary.total_spend) * 100 : 0;
                const active = commodity === c.commodity;
                return (
                  <button
                    key={c.commodity}
                    className={`relative overflow-hidden bg-card border rounded-lg p-3 text-left transition-all hover:shadow-md ${active ? 'ring-2 ring-offset-1' : ''}`}
                    style={{ borderLeftColor: color, borderLeftWidth: 4, ...(active ? { '--tw-ring-color': color } : {}) }}
                    onClick={() => setCommodity(prev => prev === c.commodity ? '' : c.commodity)}
                    data-testid={`commodity-card-${c.commodity}`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-1.5">
                        <div className="h-6 w-6 rounded-full flex items-center justify-center" style={{ backgroundColor: `${color}18` }}>
                          <Icon className="h-3.5 w-3.5" style={{ color }} />
                        </div>
                        <span className="text-[11px] font-semibold text-foreground truncate">{c.commodity}</span>
                      </div>
                      <span className="text-[9px] font-medium opacity-70">{pct.toFixed(0)}%</span>
                    </div>
                    <p className="text-lg font-bold leading-tight tracking-tight" style={{ color }}>{fmt(c.total_spend)}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{c.count.toLocaleString()} bills</p>
                    <div className="absolute inset-x-0 bottom-0 h-1" style={{ backgroundColor: color, opacity: 0.6, width: `${pct}%` }} />
                  </button>
                );
              })}
            </div>
          )}

          {/* ─── Row 3 · Monthly Bar + Donut ─── */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-card border rounded-lg p-4" data-testid="monthly-spend-chart">
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
                Monthly Spend by Commodity
              </h3>
              {monthly && monthly.data?.length > 0 ? (
                <ResponsiveContainer width="100%" height={290}>
                  <BarChart data={monthly.data} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: 10 }}
                      tickFormatter={(v) => {
                        const [y, m] = v.split('-');
                        const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                        return `${months[parseInt(m, 10) - 1] || m} '${y.slice(2)}`;
                      }}
                    />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={fmt} />
                    <RTooltip
                      formatter={(val) => fmtFull(val)}
                      labelFormatter={(v) => {
                        const [y, m] = v.split('-');
                        const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
                        return `${months[parseInt(m, 10) - 1] || m} ${y}`;
                      }}
                      contentStyle={{ fontSize: 11 }}
                    />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    {(monthly.commodities || [])
                      .filter(c => ['Electricity','Gas','Water','Sewer','Trash'].includes(c))
                      .map(c => (
                        <Bar key={c} dataKey={c} stackId="a" fill={COMMODITY_COLORS[c] || '#94a3b8'} />
                      ))}
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[290px] flex items-center justify-center text-muted-foreground text-xs">
                  {loading ? 'Loading…' : 'No monthly data available'}
                </div>
              )}
            </div>

            <div className="bg-card border rounded-lg p-4" data-testid="commodity-pie-chart">
              <h3 className="text-sm font-semibold mb-3">Spend Distribution</h3>
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={290}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      innerRadius={55}
                      paddingAngle={2}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                      style={{ fontSize: 9 }}
                    >
                      {pieData.map((entry) => (
                        <Cell key={entry.name} fill={COMMODITY_COLORS[entry.name] || '#94a3b8'} />
                      ))}
                    </Pie>
                    <RTooltip formatter={(val) => fmtFull(val)} contentStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[290px] flex items-center justify-center text-muted-foreground text-xs">No data</div>
              )}
            </div>
          </div>

          {/* ─── Row 4 · Spend Benchmark + Recent Bills + Top Vendors (equal-height) ─── */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            {/* Spend by Property (benchmark, 4 cols) */}
            <div className="lg:col-span-4 bg-card border rounded-lg flex flex-col" data-testid="spend-by-property">
              <div className="px-4 py-3 border-b flex items-center justify-between sticky top-0 bg-card z-10 rounded-t-lg">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-violet-600" />
                  Spend by Property
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge variant="outline" className="text-[9px] bg-violet-50 text-violet-700 border-violet-200 ml-1 cursor-help" data-testid="benchmark-badge">
                        <Trophy className="h-2.5 w-2.5 mr-0.5" />Per-commodity
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent className="text-xs max-w-[260px]">
                      Each property&apos;s commodities are benchmarked against the <strong>leader</strong> — the top-spending property for that commodity.
                    </TooltipContent>
                  </Tooltip>
                </h3>
                <span className="text-[10px] text-muted-foreground">
                  {properties.length} / {propMeta.total}
                </span>
              </div>
              {propMeta.commodity_leaders && propMeta.commodity_leaders.length > 0 && (
                <div className="px-4 py-2 border-b bg-muted/20 text-[10px]" data-testid="commodity-leaders-strip">
                  <div className="flex items-center gap-1 text-muted-foreground font-semibold uppercase tracking-wider mb-1">
                    <Trophy className="h-3 w-3 text-amber-500" /> Commodity leaders
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {propMeta.commodity_leaders.slice(0, 6).map(c => (
                      <Tooltip key={c.commodity}>
                        <TooltipTrigger asChild>
                          <Badge
                            variant="outline"
                            className="text-[9px] px-1.5 py-0.5 font-normal cursor-help"
                            style={{ borderColor: `${COMMODITY_COLORS[c.commodity] || '#94a3b8'}60`, color: COMMODITY_COLORS[c.commodity] || '#475569' }}
                            data-testid={`leader-${c.commodity}`}
                          >
                            <span className="font-semibold">{c.commodity}</span>
                            <span className="ml-1 font-mono">{fmt(c.max_spend)}</span>
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent className="text-xs max-w-[240px]">
                          <strong>{c.leader}</strong> leads {c.commodity} at {fmtFull(c.max_spend)}.
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </div>
                </div>
              )}
              <div className="divide-y" data-testid="property-list">
                {properties.map((p, i) => {
                  const pctOfMax = propMeta.max_spend ? (p.total_spend / propMeta.max_spend) * 100 : 0;
                  // Per-commodity benchmark: top 3 commodities for this property
                  const commEntries = Object.entries(p.commodities || {})
                    .map(([name, st]) => ({ name, ...st }))
                    .sort((a, b) => (b.spend || 0) - (a.spend || 0))
                    .slice(0, 4);
                  return (
                    <button
                      key={`${p.property}-${i}`}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-muted/30 transition-colors text-left"
                      onClick={() => goToBills({ property_search: p.property })}
                      data-testid={`property-row-${i}`}
                    >
                      <div className="w-5 text-[10px] font-semibold text-muted-foreground">{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-xs font-medium truncate" title={p.property}>{p.property}</p>
                          <span className="text-xs font-bold font-mono whitespace-nowrap">{fmt(p.total_spend)}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] text-muted-foreground truncate">
                            {p.city}{p.state ? `, ${p.state}` : ''}
                          </span>
                          <span className="text-[10px] text-muted-foreground">· {p.bill_count} bills</span>
                        </div>
                        {/* Per-commodity benchmarks */}
                        <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`commodity-benchmarks-${i}`}>
                          {commEntries.map(c => {
                            const pct = c.pct_of_leader || 0;
                            const isLeader = c.is_leader;
                            const color = COMMODITY_COLORS[c.name] || '#94a3b8';
                            return (
                              <Tooltip key={c.name}>
                                <TooltipTrigger asChild>
                                  <span
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9px] cursor-help"
                                    style={{
                                      borderColor: `${color}55`,
                                      backgroundColor: isLeader ? `${color}15` : 'transparent',
                                    }}
                                  >
                                    <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
                                    <span className="font-medium" style={{ color }}>{c.name}</span>
                                    {isLeader ? (
                                      <span className="inline-flex items-center gap-0.5 font-bold" style={{ color }}>
                                        <Trophy className="h-2 w-2" />#1
                                      </span>
                                    ) : (
                                      <span className="text-muted-foreground">{pct.toFixed(0)}%</span>
                                    )}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent className="text-xs max-w-[240px]">
                                  <div className="font-semibold">{c.name}</div>
                                  <div>This property: {fmtFull(c.spend)}</div>
                                  {isLeader ? (
                                    <div className="text-emerald-600 font-semibold">Top-spending property for {c.name}</div>
                                  ) : (
                                    <div>{pct.toFixed(0)}% of leader&apos;s spend for {c.name}</div>
                                  )}
                                </TooltipContent>
                              </Tooltip>
                            );
                          })}
                        </div>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex items-center justify-between mt-1 cursor-help">
                              <div className="flex gap-0.5 h-1 bg-muted rounded-full overflow-hidden flex-1 mr-2">
                                <div className="bg-violet-500 rounded-full transition-all" style={{ width: `${pctOfMax}%` }} />
                              </div>
                              <span className="text-[9px] text-muted-foreground">{pctOfMax.toFixed(0)}%</span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent className="text-xs max-w-[220px]">
                            Total spend = {pctOfMax.toFixed(0)}% of the top-spending property ({fmtFull(propMeta.max_spend)}).
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <ArrowUpRight className="h-3 w-3 text-muted-foreground opacity-50 flex-shrink-0" />
                    </button>
                  );
                })}
                {!propLoading && properties.length === 0 && (
                  <div className="p-8 text-center text-xs text-muted-foreground">No property data</div>
                )}
              </div>
              {(propMeta.has_more || propLoading) && (
                <div className="border-t px-4 py-2 flex items-center justify-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-[11px] gap-1"
                    onClick={() => fetchPropertiesPage(false)}
                    disabled={propLoading}
                    data-testid="property-load-more"
                  >
                    {propLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <ChevronsDown className="h-3 w-3" />}
                    {propLoading ? 'Loading…' : `Load more (${propMeta.total - properties.length} left)`}
                  </Button>
                </div>
              )}
              {!propMeta.has_more && properties.length > 0 && (
                <div className="border-t text-center py-2 text-[10px] text-muted-foreground">— end of list —</div>
              )}
            </div>

            {/* Recent Bills by Property (4 cols) */}
            <div className="lg:col-span-4 bg-card border rounded-lg flex flex-col" data-testid="recent-bills-by-property">
              <div className="px-4 py-3 border-b flex items-center justify-between sticky top-0 bg-card z-10 rounded-t-lg">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-blue-600" />
                  Recent Bills
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="text-xs max-w-[240px]">
                      Latest bill per property+account. Badge shows the occurrence count (1st = first bill on this account, 5+ = five or more billed).
                    </TooltipContent>
                  </Tooltip>
                </h3>
                <span className="text-[10px] text-muted-foreground">{recent.length} / {recentMeta.total}</span>
              </div>
              <div className="divide-y" data-testid="recent-list">
                {recent.map((r, i) => {
                  const Icon = COMMODITY_ICONS[r.commodity] || FileText;
                  const color = COMMODITY_COLORS[r.commodity] || '#94a3b8';
                  return (
                    <button
                      key={`${r.account_number}-${r.bill_id}-${i}`}
                      className="w-full flex items-start gap-2 px-3 py-2.5 hover:bg-muted/30 transition-colors text-left"
                      onClick={() => goToBills({ account_number: r.account_number, property_search: r.property })}
                      data-testid={`recent-row-${i}`}
                    >
                      <div className="h-7 w-7 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}18` }}>
                        <Icon className="h-3.5 w-3.5" style={{ color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-1">
                          <p className="text-[11px] font-medium truncate" title={r.property}>{r.property}</p>
                          <span className="text-xs font-bold font-mono whitespace-nowrap">{fmt(r.total_amount_due)}</span>
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-[10px] font-mono text-muted-foreground truncate">#{r.account_number}</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge
                                variant="outline"
                                className={`text-[8px] px-1 py-0 cursor-help ${r.occurrence_rank === '1st' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : r.occurrence_rank === '2nd' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-violet-50 text-violet-700 border-violet-200'}`}
                              >
                                {r.occurrence_rank}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent className="text-xs max-w-[220px]">
                              {r.occurrence_count === 1 ? 'First bill seen for this account.' : `${r.occurrence_count} bills seen on this account so far.`}
                            </TooltipContent>
                          </Tooltip>
                          {r.is_unit_bill && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge variant="outline" className="text-[8px] px-1 py-0 bg-indigo-50 text-indigo-700 border-indigo-200 cursor-help">Unit</Badge>
                              </TooltipTrigger>
                              <TooltipContent className="text-xs">Unit-level bill (vs common area).</TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                        <div className="flex items-center justify-between mt-0.5">
                          <span className="text-[10px] text-muted-foreground truncate">{r.vendor_name || '—'}</span>
                          <span className="text-[10px] text-muted-foreground whitespace-nowrap">{fmtShortDate(r.invoice_date)}</span>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-[10px] text-muted-foreground">New: <span className="font-mono font-medium text-foreground">{fmt(r.current_bill_amount)}</span></span>
                          <ArrowUpRight className="h-3 w-3 text-muted-foreground opacity-50" />
                        </div>
                      </div>
                    </button>
                  );
                })}
                {!recentLoading && recent.length === 0 && (
                  <div className="p-8 text-center text-xs text-muted-foreground">No recent bills</div>
                )}
              </div>
              {(recentMeta.has_more || recentLoading) && (
                <div className="border-t px-4 py-2 flex items-center justify-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-[11px] gap-1"
                    onClick={() => fetchRecentPage(false)}
                    disabled={recentLoading}
                    data-testid="recent-load-more"
                  >
                    {recentLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <ChevronsDown className="h-3 w-3" />}
                    {recentLoading ? 'Loading…' : `Load more (${recentMeta.total - recent.length} left)`}
                  </Button>
                </div>
              )}
              {!recentMeta.has_more && recent.length > 0 && (
                <div className="border-t text-center py-2 text-[10px] text-muted-foreground">— end of list —</div>
              )}
            </div>

            {/* Top Vendors by Spend (4 cols) */}
            <div className="lg:col-span-4 bg-card border rounded-lg flex flex-col" data-testid="top-vendors">
              <div className="px-4 py-3 border-b flex items-center justify-between sticky top-0 bg-card z-10 rounded-t-lg">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Truck className="h-4 w-4 text-amber-600" />
                  Top Vendors by Spend
                </h3>
                {vendors?.vendors?.length > 0 && (
                  <span className="text-[10px] text-muted-foreground">{vendors.vendors.length} vendors</span>
                )}
              </div>
              <div className="divide-y">
                {(vendors?.vendors || []).map((v, i) => {
                  const maxSpend = vendors.vendors[0]?.total_spend || 1;
                  const pct = (v.total_spend / maxSpend) * 100;
                  return (
                    <div
                      key={v.vendor}
                      className="px-4 py-2.5 text-xs hover:bg-muted/20 transition-colors"
                      data-testid={`vendor-row-${i}`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-[10px] font-semibold text-muted-foreground w-4">{i + 1}</span>
                          <span className="font-medium truncate" title={v.vendor}>{v.vendor}</span>
                        </div>
                        <span className="font-bold font-mono whitespace-nowrap">{fmt(v.total_spend)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="w-full bg-muted/40 rounded-full h-1.5 overflow-hidden cursor-help">
                                <div
                                  className="h-1.5 rounded-full bg-gradient-to-r from-amber-400 to-orange-500"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="text-xs max-w-[220px]">
                              {pct.toFixed(0)}% of the top vendor ({fmt(maxSpend)}).
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <div className="flex items-center gap-1">
                          {v.commodities.slice(0, 4).map(c => (
                            <Tooltip key={c}>
                              <TooltipTrigger asChild>
                                <span
                                  className="inline-block h-2 w-2 rounded-full cursor-help"
                                  style={{ backgroundColor: COMMODITY_COLORS[c] || '#94a3b8' }}
                                />
                              </TooltipTrigger>
                              <TooltipContent className="text-xs">{c}</TooltipContent>
                            </Tooltip>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-foreground">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="flex items-center gap-1 cursor-help" data-testid={`vendor-accounts-${i}`}>
                              <Hash className="h-2.5 w-2.5" />
                              <span className="font-semibold text-foreground">{(v.account_count ?? 0).toLocaleString()}</span>
                              accounts
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="text-xs">Distinct utility account numbers serviced by this vendor.</TooltipContent>
                        </Tooltip>
                        <span className="flex items-center gap-1">
                          <FileText className="h-2.5 w-2.5" />
                          <span className="font-semibold text-foreground">{v.bill_count.toLocaleString()}</span>
                          bills
                        </span>
                      </div>
                    </div>
                  );
                })}
                {(!vendors?.vendors || vendors.vendors.length === 0) && !loading && (
                  <div className="p-6 text-center text-xs text-muted-foreground">No vendor data available</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </TooltipProvider>
    </MainLayout>
  );
}

function KpiCard({ label, value, icon, sub, tip, accent = 'from-blue-500 to-blue-600', testId }) {
  return (
    <div className="relative overflow-hidden border rounded-lg p-3 bg-card hover:shadow-md transition-shadow" data-testid={testId}>
      <div className={`absolute top-0 right-0 h-8 w-8 rounded-bl-full bg-gradient-to-br ${accent} opacity-15`} />
      <div className="flex items-center gap-2 mb-1 relative">
        <div className={`h-6 w-6 rounded-md bg-gradient-to-br ${accent} flex items-center justify-center text-white shadow-sm`}>
          {icon}
        </div>
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
        {tip && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="h-3 w-3 text-muted-foreground opacity-60 cursor-help ml-auto" />
            </TooltipTrigger>
            <TooltipContent className="text-xs max-w-[260px]">{tip}</TooltipContent>
          </Tooltip>
        )}
      </div>
      <p className="text-lg font-bold tracking-tight">{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}
